import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useInterventionCreation } from '../../context/interventionCreationContext';

export type DeleteRequestState = {
  deleteRequest: (id: number) => void;
  /** Suppression en masse : l'API a son propre endpoint pour une liste. */
  deleteRequests: (ids: number[]) => void;
  isDeleting: boolean;
  hasFailed: boolean;
  dismissError: () => void;
};

/**
 * Supprime des demandes d'intervention, une seule ou toute la sélection.
 *
 * Deux endpoints distincts — `DELETE {id}/` et `POST delete-pds-list/` — mais un seul
 * hook : le nettoyage de la sélection et l'invalidation de la liste sont identiques, et
 * l'appelant n'a qu'un état de chargement à surveiller.
 */
export function useDeleteRequest(): DeleteRequestState {
  const { state, dispatch } = useInterventionCreation();
  const queryClient = useQueryClient();

  const customerParams = () => {
    if (state.clientId === null) {
      throw new Error('Aucun client sélectionné');
    }

    return new URLSearchParams({ customer_id: String(state.clientId) });
  };

  /**
   * Une demande supprimée ne doit pas rester cochée, sinon la qualification partirait
   * avec un identifiant qui n'existe plus.
   *
   * Le bilan se publie ici, et non dans chaque mutation : les deux suppressions passent
   * par cette fonction, qui connaît déjà le nombre de demandes retirées.
   */
  const forgetDeleted = (deletedIds: number[]) => {
    dispatch({
      type: 'setSelectedRequests',
      ids: state.selectedRequestIds.filter((selectedId) => !deletedIds.includes(selectedId))
    });
    queryClient.invalidateQueries(['exocet-pds-to-qualify']);
    dispatch({ type: 'reportDeletion', report: { deletedCount: deletedIds.length } });
  };

  const deleteOne = useMutation<void, unknown, number>({
    mutationFn: async (id) => {
      await axios.delete(`/api/boi/exocet/pds/qualify/${id}/?${customerParams()}`);
    },
    onSuccess: (_result, id) => forgetDeleted([id])
  });

  const deleteMany = useMutation<void, unknown, number[]>({
    mutationFn: async (ids) => {
      await axios.post(`/api/boi/exocet/pds/qualify/delete-pds-list/?${customerParams()}`, {
        pds_ids: ids
      });
    },
    onSuccess: (_result, ids) => forgetDeleted(ids)
  });

  return {
    deleteRequest: deleteOne.mutate,
    deleteRequests: deleteMany.mutate,
    isDeleting: deleteOne.isLoading || deleteMany.isLoading,
    hasFailed: deleteOne.isError || deleteMany.isError,
    dismissError: () => {
      deleteOne.reset();
      deleteMany.reset();
    }
  };
}
